import { useCallback } from "react";
import { Icon } from "../ui/Icon";
import HeaderIconButton from "./HeaderIconButton";
import { useLauncherContext } from "@/app/[tenantId]/[messengerId]/[hostConfig]/lib/client/contexts/LauncherContext";
import { getExpandedIframeDimensions } from "@/app/[tenantId]/[messengerId]/[hostConfig]/lib/client/utils/getIframeDimensions";
import { useLauncherStore } from "@/app/[tenantId]/[messengerId]/[hostConfig]/lib/client/contexts/LauncherStoreContext";
import { useShallow } from "zustand/react/shallow";

type ExpandButtonProps = {
  messengerId: string;
};

export const ExpandButton = ({ messengerId }: ExpandButtonProps) => {
  const isExpanded = useLauncherStore(({ isExpanded }) => isExpanded);
  const toggleExpand = useToggleExpand(messengerId);

  return (
    <HeaderIconButton onClick={() => toggleExpand()}>
      <Icon
        icon={isExpanded ? "dazzle-linear-compress-alt" : "dazzle-linear-expand-alt"}
        size="text-xl"
      />
    </HeaderIconButton>
  );
};

export const useToggleExpand = (messengerId: string, isCompactExpanded = false) => {
  const { showLauncherButton, config, isExpanded, setIsExpanded } = useLauncherStore(
    useShallow((state) => ({
      showLauncherButton: state.showLauncherButton,
      isExpanded: state.isExpanded,
      setIsExpanded: state.setIsExpanded,
      config: state.config,
      goTo: state.goTo,
      isFullscreen: state.isFullscreen,
    })),
  );
  const { connection } = useLauncherContext();

  const offsetBottom = config?.settings?.displaySettings.placement?.offsetBottom ?? 0;
  const offsetSide = config?.settings?.displaySettings.placement?.offsetSide ?? 0;

  return useCallback(
    async (type?: "expand" | "collapse") => {
      if (!connection?.current) return;
      const parentApi = await connection.current.promise;
      const parentViewDimensions = await parentApi.getViewDimensions(messengerId);
      const expand = type === "expand" ? true : type === "collapse" ? false : !isExpanded;
      const data = getExpandedIframeDimensions({
        isOpen: true,
        isExpanded: expand,
        isCompactExpanded,
        showLauncherButton,
        offsetBottom,
        offsetSide,
        parentViewDimensions,
      });

      expand
        ? parentApi.expand(messengerId, {
            ...data,
            isCompactExpanded,
          })
        : parentApi.collapse(messengerId, {
            ...data,
            isCompactExpanded,
          });
      setIsExpanded(expand);
    },
    [
      connection,
      isExpanded,
      messengerId,
      offsetBottom,
      offsetSide,
      setIsExpanded,
      showLauncherButton,
      isCompactExpanded,
    ],
  );
};
